import React, { useEffect, useState } from "react";
import useQuery from "../../../../hooks/useQuery";
import { Button, Segmented, Space } from "antd";
import { getDischargeSummaryApi } from "../../../../services/discharge.services";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import dischargeImage from "../../../../assets/images/nurseIcons/dischargeSummary.svg";
import FormContentLoader from "../../FormsIp/FormContentLoader";
import ShareModal from "./ShareModal";
import ScaledHtmlPreview from "../../../ui/ScaledHtmlPreview";
import useIsMobile from "../../../../hooks/useIsMobile";
import "./DischargeSummaryToolbar.css";
import "./DischargeSummaryMobile.css";

const ROLE_OPTIONS = [
  { label: "Doctor", value: "Doctor" },
  { label: "Physiotherapist", value: "Physiotherapist" },
];

const ReceptionistDischargeSummary = ({ patientData }) => {
  const params = useQuery();
  const isMobile = useIsMobile();
  const [currTab, setCurrTab] = useState("Doctor");
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (!params?.patient) return;

    let cancelled = false;
    setLoading(true);

    getDischargeSummaryApi(params.patient, `${currTab}&print=True`)
      .then((res) => {
        if (!cancelled) {
          setSummaryData(res?.data?.data ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummaryData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currTab, params?.patient]);

  const onChange = (value) => {
    if (value === currTab) return;
    setSummaryData(null);
    setCurrTab(value);
  };

  const printDischargeSummary = async () => {
    try {
      const resp = await getDischargeSummaryApi(
        params?.patient,
        `${currTab}&print=True`
      );
      if (resp && resp?.data?.data) {
        const printDocument = document.createElement("div");
        printDocument.innerHTML = resp?.data?.data;

        const iframe = document.createElement("iframe");
        document.body.appendChild(iframe);

        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printDocument.outerHTML);
        iframeDoc.close();

        setTimeout(() => {
          iframe.contentWindow.print();
          document.body.removeChild(iframe);
        }, 300);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const actionsDisabled = loading || !summaryData;

  return (
    <div className="discharge-summary-view">
      <div className="ds-toolbar">
        <Segmented
          className="ds-role-segment"
          options={ROLE_OPTIONS}
          value={currTab}
          onChange={onChange}
        />

        <div className="ds-toolbar__actions">
          <Button
            type="primary"
            className="ds-action-btn"
            onClick={() => setOpenModal(true)}
            disabled={actionsDisabled}
          >
            {isMobile ? "Share Summary" : "Share Discharge Summary"}
          </Button>
          <Button
            className="ds-action-btn ds-action-btn--secondary"
            onClick={printDischargeSummary}
            disabled={actionsDisabled}
          >
            {isMobile ? "Print Summary" : "Print Discharge Summary"}
          </Button>
        </div>
      </div>

      <FormContentLoader
        loading={loading}
        tip={`Loading ${currTab} discharge summary...`}
      >
        {summaryData ? (
          <>
            <ScaledHtmlPreview
              html={summaryData}
              designWidth={800}
              className="ip-form-html-preview"
            />
            {openModal && (
              <ShareModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                id={params?.patient}
                practitioner={currTab}
                patientData={patientData}
              />
            )}
          </>
        ) : (
          <NoDataPage
            title="Discharge Summary Not generated"
            image={dischargeImage}
          />
        )}
      </FormContentLoader>
    </div>
  );
};

export default ReceptionistDischargeSummary;
